import { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  formatCityAdminLabel,
  getCityAdminCities,
  isCityAdminUser,
  resolveCityAdminActiveCity,
  storeAdminActiveCityId,
} from "../helpers/cityPortalRoles"
import { setAdminActiveCityId } from "../store/slices/authSlice"

/**
 * Active city scope for city admins (`type: admin`) from assigned cities.
 */
const useAdminActiveCity = () => {
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.auth.currentUser)
  const storedCityId = useSelector(state => state.auth.adminActiveCityId)
  const isCityAdmin = isCityAdminUser(currentUser)

  const cities = useMemo(
    () => (isCityAdmin ? getCityAdminCities(currentUser) : []),
    [currentUser, isCityAdmin],
  )

  const activeCity = useMemo(
    () =>
      isCityAdmin
        ? resolveCityAdminActiveCity(currentUser, storedCityId)
        : null,
    [currentUser, isCityAdmin, storedCityId],
  )

  useEffect(() => {
    if (!isCityAdmin || !activeCity?.id) return
    if (String(storedCityId || "") === String(activeCity.id)) return

    storeAdminActiveCityId(currentUser?.id, activeCity.id)
    dispatch(setAdminActiveCityId(activeCity.id))
  }, [activeCity?.id, currentUser?.id, dispatch, isCityAdmin, storedCityId])

  const setActiveCityId = cityId => {
    if (!cityId) return
    storeAdminActiveCityId(currentUser?.id, cityId)
    dispatch(setAdminActiveCityId(cityId))
  }

  return {
    isCityAdmin,
    cities,
    activeCity,
    activeCityId: activeCity?.id || "",
    activeCityLabel: formatCityAdminLabel(activeCity),
    setActiveCityId,
  }
}

export default useAdminActiveCity
